import {
  type AskPunttMessage,
  type AskPunttProjectData,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowUp,
  ArrowsOutSimple,
  CaretRight,
  CircleNotch,
} from "@phosphor-icons/react";
import { Badge, Button, Flex, Text, TextArea } from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { askPunttChatRoute } from "./chatRoute";
import {
  AssetViewerPane,
  type AssetViewerPaneHandle,
} from "./components/AssetViewerPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasLoadingPane } from "./components/CanvasLoadingPane";
import { CanvasPane } from "./components/CanvasPane";
import { ChatMessage, RobotIcon } from "./components/ChatMessage";
import {
  InputPlusMenu,
  type ConnectorOption,
} from "./components/InputPlusMenu";
import { ProjectReviewStatusBanner } from "./components/ProjectReviewStatusBanner";
import { ProjectStatusPane } from "./components/ProjectStatusPane";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { TicketViewPane } from "./components/TicketViewPane";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useTicketViewData } from "./hooks/useTicketView";
import { askPunttRoute } from "./route";

import { getGuidelines } from "../../services/brand-guidelines";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import {
  useAskPunttChatHistory,
  useAskPunttStreamingState,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";

const STREAM_POLL_MS = 500;
const FULL_POLL_MS = 3000;
const POLL_TIMEOUT_MS = 300_000;

const SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
  project: "Project",
};

/** Convert chat's enabledConnectors array to Record for display */
function connectorsArrayToRecord(
  ids: string[] | undefined,
  available: ConnectorOption[],
): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  available.forEach((c) => {
    record[c.id] = ids?.includes(c.id) ?? false;
  });
  return record;
}

export default function AskPunttChat() {
  const { chatId } = askPunttChatRoute.useParams();
  const [waitingForAI, setWaitingForAI] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);

  const [reviewPolling, setReviewPolling] = useState(false);

  // Full document poll: 1s until we find the pending message, then slow to 3s
  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: waitingForAI
      ? pendingMessageId
        ? FULL_POLL_MS
        : STREAM_POLL_MS
      : reviewPolling
        ? FULL_POLL_MS
        : false,
  });

  // Lightweight streaming poll: only the pending message's streaming fields
  const { data: streamingState } = useAskPunttStreamingState(
    chatId,
    pendingMessageId,
    {
      enabled: !!pendingMessageId && waitingForAI,
      refetchInterval: STREAM_POLL_MS,
    },
  );
  const sendMessage = useSendAskPunttMessage(chatId);
  const queryClient = useQueryClient();
  const analytics = useAnalytics("ask_puntt_chat");
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      (guidelinesQuery.data?.askPunttConnectors ?? [])
        .filter((c) => c.enabled && c.connected)
        .map((c) => ({
          id: c.connectorId,
          label: `${c.name} search`,
          icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
        })),
    [guidelinesQuery.data?.askPunttConnectors],
  );

  const [inputMessage, setInputMessage] = useState("");
  const [localMessages, setLocalMessages] = useState<AskPunttMessage[]>([]);
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles({
    onUploadError: (err) =>
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      }),
  });
  const [canvasDismissed, setCanvasDismissed] = useState(false);
  const [ticketDismissed, setTicketDismissed] = useState(false);
  const [focusedComment, setFocusedComment] = useState<{
    id: string;
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(true);
  const knownMessageCountRef = useRef(0);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);
  const prevDataUpdatedAtRef = useRef<number>(0);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
      setPendingMessageId(null);
    }
  }, [waitingForAI]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      const messages = chatData.messages;
      knownMessageCountRef.current = messages.length;
      setLocalMessages(messages);
      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" ||
          (last.role === "assistant" &&
            (last.status === "pending" || !last.content)))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    const dataChanged = dataUpdatedAt !== prevDataUpdatedAtRef.current;
    if (dataChanged) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setLocalMessages(chatData.messages);
      knownMessageCountRef.current = chatData.messages.length;
    }

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    if (!dataChanged) return;

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.content &&
      lastMsg.status !== "pending"
    ) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      // Reset canvas/ticket dismissed state when new response arrives
      if (lastMsg.canvasContent) {
        setCanvasDismissed(false);
      }
      if (lastMsg.viewTicketId) {
        setTicketDismissed(false);
        setReviewPolling(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  // Detect the pending assistant message ID for lightweight streaming poll
  useEffect(() => {
    if (!waitingForAI || !chatData?.messages || pendingMessageId) return;
    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      (lastMsg.status === "pending" || !lastMsg.content)
    ) {
      setPendingMessageId(lastMsg._id);
    }
  }, [chatData?.messages, waitingForAI, pendingMessageId]);

  // Merge lightweight streaming state into localMessages for faster UI updates
  useEffect(() => {
    if (!streamingState || !pendingMessageId) return;
    setLocalMessages((prev) =>
      prev.map((msg) => {
        if (msg._id !== pendingMessageId) return msg;
        // Spread streaming fields over the existing message. The streaming
        // endpoint returns a subset of AskPunttMessage fields — TypeScript's
        // structural overlap is safe here because the Zod schemas match.
        const patch: Partial<AskPunttMessage> = {
          status: streamingState.status as AskPunttMessage["status"],
          streamingContent: streamingState.streamingContent,
          canvasLoading: streamingState.canvasLoading,
          suggestedResponses: streamingState.suggestedResponses,
        };
        if (streamingState.content) patch.content = streamingState.content;
        if (streamingState.agentSteps?.length)
          patch.agentSteps =
            streamingState.agentSteps as AskPunttMessage["agentSteps"];
        if (streamingState.searchProgress?.length)
          patch.searchProgress =
            streamingState.searchProgress as AskPunttMessage["searchProgress"];
        if (streamingState.canvasContent)
          patch.canvasContent =
            streamingState.canvasContent as AskPunttMessage["canvasContent"];
        if (streamingState.viewTicketId)
          patch.viewTicketId = streamingState.viewTicketId;
        if (streamingState.projectData)
          patch.projectData =
            streamingState.projectData as AskPunttMessage["projectData"];
        return { ...msg, ...patch };
      }),
    );
  }, [streamingState, pendingMessageId]);

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });
  }, [chatId]);

  // Auto-scroll to bottom when messages or stream state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages, waitingForAI, streamingState]);

  // Detect the latest canvas content from messages
  const activeCanvas = useMemo((): {
    canvas: CanvasContent;
    skill?: string;
  } | null => {
    if (canvasDismissed) return null;
    // Find the last assistant message with canvasContent
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return {
          canvas: msg.canvasContent as CanvasContent,
          skill: msg.activeSkill ?? undefined,
        };
      }
    }
    return null;
  }, [localMessages, canvasDismissed]);

  // Check if any message has canvas content (regardless of dismissed state)
  const hasCanvasContent = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [localMessages]);

  // Detect canvas loading state (agent is generating canvas content)
  const isCanvasLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.canvasLoading);
  }, [localMessages, waitingForAI]);

  // Detect the latest viewTicketId from messages
  const activeViewTicketId = useMemo((): string | null => {
    if (ticketDismissed) return null;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.viewTicketId) return msg.viewTicketId;
    }
    return null;
  }, [localMessages, ticketDismissed]);

  // Lift ticket data to parent to determine layout mode
  const {
    comments: viewComments,
    isLoading: viewTicketLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(activeViewTicketId, {
    pollingEnabled: reviewPolling,
  });

  // Detect project data from chat-level or latest message
  const activeProjectData = useMemo((): AskPunttProjectData | null => {
    const chatLevel = chatData?.chat?.projectData as
      | AskPunttProjectData
      | null
      | undefined;
    if (chatLevel) return chatLevel;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.projectData) {
        return msg.projectData as AskPunttProjectData;
      }
    }
    return null;
  }, [chatData?.chat?.projectData, localMessages]);

  const isProjectChat = chatData?.chat?.workflowType === "project";

  const use3PaneLayout =
    !!activeViewTicketId && hasContextAssets && !isProjectChat;

  const handleDismissCanvas = useCallback(() => {
    setCanvasDismissed(true);
  }, []);

  const handleDismissTicket = useCallback(() => {
    setTicketDismissed(true);
  }, []);

  const labelSheetRef = useRef<AssetViewerPaneHandle>(null);

  const handleCitationClick = useCallback((pageNumber: number) => {
    labelSheetRef.current?.navigateToPage(pageNumber);
  }, []);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setFocusedComment({
          id: comment._id ?? "",
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        // Auto-clear after 3 seconds
        focusTimerRef.current = setTimeout(() => setFocusedComment(null), 3000);
      }
    },
    [],
  );

  const handleSend = async (
    overrideMessage?: string,
    extraAttachments?: {
      key: string;
      source: string;
      type: "image" | "video" | "file";
      name?: string;
    }[],
  ) => {
    const text = overrideMessage ?? inputMessage;
    const allAttachments = [...readyAttachments, ...(extraAttachments ?? [])];
    if (
      (!text.trim() && allAttachments.length === 0) ||
      sendMessage.isPending ||
      hasPending
    )
      return;

    const messageToSend = text;
    const attachments = allAttachments;
    const messageLength = messageToSend.length;
    const messageIndex = localMessages.filter((m) => m.role === "user").length;

    const userMsg: AskPunttMessage = {
      _id: `local-user-${Date.now()}`,
      chatId,
      role: "user",
      content: messageToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };

    setLocalMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    clearAll();

    try {
      const result = await sendMessage.mutateAsync({
        message: messageToSend,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Replace the optimistic user message with the server-confirmed one
      setLocalMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
        return [...withoutOptimistic, result.userMessage];
      });
      knownMessageCountRef.current = localMessages.length;
      dataAtWaitStartRef.current = dataUpdatedAt;
      setWaitingForAI(true);

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_sent", {
        chat_id: chatId,
        message_length: messageLength,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        message_index: messageIndex,
        workflow_id: result.workflowId,
      });
    } catch (error) {
      setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_send_error", {
        chat_id: chatId,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to send message:", error);
    }
  };

  const chat = chatData?.chat;
  const reviewStatus = chat?.reviewStatus ?? null;

  // Sync review polling state with reviewStatus from server
  useEffect(() => {
    setReviewPolling(!!reviewStatus);
  }, [reviewStatus]);

  // When review polling stops, force a final fetch to pick up any comments
  // saved in the window between the last poll and polling stopping.
  const prevReviewPollingRef = useRef(false);
  useEffect(() => {
    if (prevReviewPollingRef.current && !reviewPolling && activeViewTicketId) {
      queryClient.invalidateQueries({
        queryKey: ["ticket-view", activeViewTicketId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ticket-view-comments", activeViewTicketId],
      });
    }
    prevReviewPollingRef.current = reviewPolling;
  }, [reviewPolling, activeViewTicketId, queryClient]);

  // Find the active skill label from the last assistant message
  const activeSkillLabel = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.activeSkill) {
        return SKILL_LABELS[msg.activeSkill];
      }
    }
    return undefined;
  }, [localMessages]);

  // Extract suggested responses from the last assistant message
  const activeSuggestedResponses = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.suggestedResponses &&
      lastMsg.suggestedResponses.length > 0
    ) {
      return lastMsg.suggestedResponses;
    }
    return null;
  }, [localMessages]);

  // Hide suggestion pills when the last message has userQuestions (the form handles it)
  const lastMessage = localMessages[localMessages.length - 1];
  const hasQuestionForm =
    lastMessage?.role === "assistant" &&
    lastMessage.userQuestions &&
    lastMessage.userQuestions.length > 0;
  const showSuggestions =
    activeSuggestedResponses &&
    !waitingForAI &&
    !sendMessage.isPending &&
    !hasQuestionForm;

  return (
    <div
      className="flex h-full flex-col overflow-hidden bg-puntt-neutral-gray-2"
      data-testid="ask-puntt-chat"
    >
      {/* Breadcrumb */}
      <Flex
        align="center"
        gap="1"
        className="shrink-0 border-b border-puntt-neutral-gray-4 bg-base-white/90 px-5 py-3.5 backdrop-blur-sm"
      >
        <Link to={askPunttRoute.to} className="puntt-link">
          <Text
            size="2"
            weight="medium"
            className="text-puntt-accent-11 hover:underline"
          >
            Ask Puntt
          </Text>
        </Link>
        {chat?.title && (
          <>
            <CaretRight size={12} className="text-puntt-neutral-gray-9" />
            <Text
              size="2"
              weight="medium"
              className="max-w-[400px] truncate text-puntt-neutral-gray-12"
            >
              {chat.title}
            </Text>
          </>
        )}
        {activeSkillLabel && (
          <Badge size="1" variant="soft" color="blue" className="ml-2">
            {activeSkillLabel}
          </Badge>
        )}
        {canvasDismissed && hasCanvasContent && (
          <Button
            size="1"
            variant="soft"
            color="blue"
            className="ml-auto cursor-pointer"
            onClick={() => setCanvasDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Canvas</Text>
          </Button>
        )}
      </Flex>

      {/* Main content: multi-pane layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chat Pane */}
        <div
          className={`flex flex-col overflow-hidden ${
            use3PaneLayout
              ? "w-1/4 border-r border-puntt-neutral-gray-5"
              : (isProjectChat && activeProjectData) ||
                  activeViewTicketId ||
                  activeCanvas ||
                  isCanvasLoading ||
                  activeProjectData
                ? "w-1/2 border-r border-puntt-neutral-gray-5"
                : "w-full"
          }`}
        >
          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto px-4 py-6"
            data-testid="ask-puntt-messages"
          >
            <div className="mx-auto max-w-[720px] space-y-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                    />
                  ))}
                </div>
              ) : (
                <>
                  {localMessages.map((message, idx) => {
                    const isLast = idx === localMessages.length - 1;
                    const isLastAssistant =
                      message.role === "assistant" && isLast && !waitingForAI;
                    return (
                      <ChatMessage
                        key={message._id}
                        message={message}
                        isStreaming={
                          waitingForAI && isLast && message.role === "assistant"
                        }
                        isLastAssistant={isLastAssistant}
                        onSubmitAnswers={handleSend}
                        formDisabled={sendMessage.isPending || waitingForAI}
                      />
                    );
                  })}
                  {waitingForAI &&
                    localMessages[localMessages.length - 1]?.role !==
                      "assistant" && (
                      <div className="flex items-center gap-3 rounded-2xl border border-puntt-neutral-gray-4/50 bg-base-white p-4 shadow-sm">
                        <div className="animate-thinking-pulse">
                          <RobotIcon />
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <Text
                            size="2"
                            weight="medium"
                            className="text-puntt-neutral-gray-11"
                          >
                            Puntt is thinking
                          </Text>
                          <span className="flex gap-0.5">
                            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
                            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
                            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
                          </span>
                        </div>
                      </div>
                    )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="shrink-0 border-t border-puntt-neutral-gray-4/50 bg-puntt-neutral-gray-2 p-4">
            <div className="mx-auto max-w-[720px] space-y-3">
              {isProjectChat && activeProjectData && (
                <ProjectReviewStatusBanner projectData={activeProjectData} />
              )}
              <div className="relative rounded-2xl border border-puntt-neutral-gray-5 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20">
                {showSuggestions && (
                  <SuggestedResponses
                    suggestions={activeSuggestedResponses}
                    onSelect={(suggestion) => handleSend(suggestion)}
                    disabled={sendMessage.isPending || waitingForAI}
                  />
                )}
                <TextArea
                  data-testid="ask-puntt-chat-reply-input"
                  size="2"
                  variant="surface"
                  placeholder="Reply..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[60px] w-full resize-none border-none !shadow-none"
                  style={{ outline: "none", boxShadow: "none" }}
                />

                {/* Attached files preview */}
                {attachedItems.length > 0 && (
                  <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                    {attachedItems.map((item, idx) => (
                      <Flex
                        key={item.id}
                        align="center"
                        gap="1"
                        className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                      >
                        {item.status === "pending" && (
                          <CircleNotch
                            size={12}
                            weight="bold"
                            className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                          />
                        )}
                        <Text
                          size="1"
                          className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                        >
                          {item.file.name}
                          {item.status === "error" && " (failed)"}
                        </Text>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                        >
                          ×
                        </button>
                      </Flex>
                    ))}
                  </Flex>
                )}

                <Flex justify="between" align="center" className="px-3 py-1.5">
                  <InputPlusMenu
                    onFilesSelected={addFiles}
                    onScreenshotCaptured={addFile}
                    enabledConnectors={connectorsArrayToRecord(
                      chat?.enabledConnectors,
                      availableConnectors,
                    )}
                    connectorsDisabled
                    availableConnectors={availableConnectors}
                  />

                  <Button
                    data-testid="ask-puntt-chat-send"
                    size="2"
                    variant={
                      inputMessage.trim() || readyAttachments.length > 0
                        ? "solid"
                        : "ghost"
                    }
                    color={
                      hasPending
                        ? "gray"
                        : inputMessage.trim() || readyAttachments.length > 0
                          ? "blue"
                          : "gray"
                    }
                    disabled={
                      (!inputMessage.trim() && readyAttachments.length === 0) ||
                      sendMessage.isPending ||
                      hasPending ||
                      waitingForAI
                    }
                    onClick={() => handleSend()}
                    className="cursor-pointer transition-all duration-200"
                  >
                    {hasPending ? (
                      <CircleNotch
                        size={14}
                        weight="bold"
                        className="animate-spin"
                      />
                    ) : (
                      <ArrowUp size={14} weight="bold" />
                    )}
                  </Button>
                </Flex>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane(s) */}
        {use3PaneLayout ? (
          <>
            {/* 3-pane: Label Sheet + Artwork */}
            <div className="w-3/8 animate-canvas-slide-in border-r border-puntt-neutral-gray-5">
              {viewTicketLoading ? (
                <div className="flex h-full items-center justify-center bg-base-white">
                  <Text size="2" className="text-puntt-neutral-gray-9">
                    Loading...
                  </Text>
                </div>
              ) : (
                <AssetViewerPane
                  ref={labelSheetRef}
                  title="Label Sheet"
                  assets={contextFiles}
                />
              )}
            </div>
            <div className="relative w-3/8 animate-canvas-slide-in">
              {viewTicketLoading ? (
                <div className="flex h-full items-center justify-center bg-base-white">
                  <Text size="2" className="text-puntt-neutral-gray-9">
                    Loading...
                  </Text>
                </div>
              ) : (
                <AssetViewerPane
                  title="Artwork"
                  assets={latestReviewFiles}
                  reviewStatus={reviewStatus}
                  comments={viewComments ?? []}
                  activeCommentId={focusedComment?.id}
                  onCommentClick={handleCommentClick}
                  onPinClick={handleCommentClick}
                  onCitationClick={handleCitationClick}
                  highlightPosition={
                    focusedComment
                      ? {
                          x: focusedComment.x,
                          y: focusedComment.y,
                          page: focusedComment.page,
                        }
                      : null
                  }
                />
              )}
            </div>
          </>
        ) : isProjectChat && activeProjectData ? (
          <div className="w-1/2 animate-canvas-slide-in">
            <ProjectStatusPane
              projectData={activeProjectData}
              activeTicketId={activeViewTicketId}
              chatCreatedAt={chatData?.chat?.createdAt}
            />
          </div>
        ) : activeViewTicketId ? (
          <div className="w-1/2 animate-canvas-slide-in">
            <TicketViewPane
              ticketId={activeViewTicketId}
              onDismiss={handleDismissTicket}
              reviewStatus={reviewStatus}
            />
          </div>
        ) : activeCanvas ? (
          <div
            className="w-1/2 animate-canvas-slide-in"
            data-testid="ask-puntt-canvas-area"
          >
            <CanvasPane
              canvas={activeCanvas.canvas}
              onDismiss={handleDismissCanvas}
              skillName={
                activeCanvas.skill
                  ? SKILL_LABELS[activeCanvas.skill]
                  : undefined
              }
              chatId={chatId}
              isUpdating={isCanvasLoading}
            />
          </div>
        ) : isCanvasLoading ? (
          <div className="w-1/2 animate-canvas-slide-in">
            <CanvasLoadingPane />
          </div>
        ) : activeProjectData ? (
          <div className="w-1/2 animate-canvas-slide-in">
            <ProjectStatusPane
              projectData={activeProjectData}
              chatCreatedAt={chatData?.chat?.createdAt}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
